// The shortlists a care circle has, and the paywall state layered onto them.
//
// These are the same shortlists the care search pane on the right of Care search
// works with, not a parallel set: the provider names come straight from that
// pane's seed items, so a provider cannot be called one thing here and another
// there. What this file adds is the paywall — whether there is capacity, and
// where the provider is in the payment lifecycle.
//
// Tier no longer decides anything here. Every provider with capacity is messaged,
// premium or not, so `tier` is data we hold about a provider rather than a gate,
// and the `direct` state it used to produce is gone.

import { SEED_ITEMS } from '@/cam-admin/care-search/CareSearch.fixtures';
import { type Status as ContactStatus } from '@/cam-admin/care-search/CareSearch.content';
import { CARE_CIRCLE } from '@/cam-admin/control-panel/fixtures';

// No comms status here. Care search keeps its own contact tracking, because
// there a CAM really is the one making the calls and setting it. On this surface
// there is one status per provider and the paywall moves it, so a second
// changeable one would be the two-status model back again.

export type Tier = 'Premium' | 'Standard';

// Where a provider sits in the paywall. One state per provider, and a CAM reads
// it rather than sets it.
//   draft        — on the list, the email has not gone out yet.
//   messaged     — email sent, nothing back yet.
//   paid         — paid, so the lead is released to them.
//   declined     — tapped "can't take this one". Just the no: we don't ask why.
//
// Declining is a real state because we ask for it in one tap, on every screen that
// shows a provider the lead. Most nos will still arrive as silence, and that is the
// point of making the no free: without it every unanswered row means no room, no
// interest and no email opened all at once, and `messaged` has to carry the lot.
//
// Capacity is not a state. Every dom provider gets messaged regardless, so a
// provider with no room says so by declining rather than by being filtered out
// before the send. `owed` is gone too: publish includes unpaid providers by
// default, so there is no separate escape-hatch state to name.
export type PaywallState = 'draft' | 'messaged' | 'paid' | 'declined';

// What kind of care a shortlist is for, and on this tab it decides everything.
// The paywall is dom care only: a dom provider is a business that will pay for a
// lead, so publishing can be the send. A care home is rung up by a CAM, has no
// lead to buy and no Stripe in the loop, so a care home list on this tab is the
// list without the paywall — no send, no payment, no visibility gate.
//
// Said as a care type rather than a `sends: boolean` because that is the fact.
// The flag would be a setting somebody could imagine turning on for care homes,
// and the spec is explicit that care requests do not map this way.
export type CareType = 'DOM care' | 'Care home';

// Whether this list runs the paywall. The one branch on the tab, and every
// difference a CAM sees follows from it.
export const usesPaywall = (l: { careType: CareType }) =>
  l.careType === 'DOM care';

// The status a provider carries on a list that is not running the paywall. The
// same four words Care search uses, imported rather than restated, because on a
// care home list a CAM really is the one ringing round and setting this.
//
// This is not the two-status model coming back. A list runs the paywall or it
// does not, so a provider carries one of these or a `state` and never both —
// which is also why they are not one union: `messaged` and `Contacted` are the
// same sentence in two different worlds, and merging them would let a care home
// row say Paid.
export type { ContactStatus };

// Who filled the answers in.
//
// It matters because the two are not worth the same. A provider tapping their own
// availability is something we can stand behind and show the carer. A CAM typing
// up what someone said on the phone is second-hand: useful for deciding who to
// publish, and never shown to the carer.
//
// Provider-entered wins. If a provider who rang last week answers the page
// themselves, their answers replace the transcription rather than sitting beside
// it, because two versions of the same fact is a question nobody can settle.
export type AnswerSource = 'provider' | 'cam';

// Who answers and the number that reaches them, and nothing else. Earliest
// start, minimum package and the complex-needs list were all here and all came
// off: the care assessment holds what the request needs, so collecting a second
// copy of it against every provider was asking somebody to retype the lead.
export type ProviderAnswers = {
  contact: string;
  directNumber: string;
  source: AnswerSource;
  /**
   * The stamp, on CAM-entered answers only. `you` is this session's CAM, matching
   * how the control panel names the current user elsewhere; a colleague's name is
   * what makes the provenance worth showing at all.
   */
  enteredBy?: string;
  enteredAt?: string;
};

export type PaywallProvider = {
  id: string;
  name: string;
  tier: Tier;
  hasSpace: boolean;
  address: string;
  phone: string;
  /**
   * Where the paywall link goes. The inbox we hold, which is usually a central
   * one — the named contact and direct number on `answers` are what we get back
   * once somebody actually opens it.
   */
  email: string;
  /** The CAM's own note. The thing a table row has no room for. */
  note?: string;
  state: PaywallState;
  /**
   * The status on a care home list, where there is no paywall to move one. Set by
   * the CAM who rang, and only ever present on a list `usesPaywall` says no to —
   * `state` is what a dom row carries.
   */
  contactState?: ContactStatus;
  /** When the link went out. Present for messaged, paid and declined. */
  sentAt?: string;
  /** When Stripe confirmed. Present for paid. */
  paidAt?: string;
  /** When they tapped the no. Present for declined. */
  declinedAt?: string;
  /**
   * When a CAM last moved this row by hand. The care home equivalent of the three
   * stamps above, and the only one those lists have: nothing else touches them.
   */
  contactedAt?: string;
  /**
   * What we hold about this provider against this request. Present once somebody
   * has answered, and `source` says who: the provider on the paywall, or a CAM
   * typing up a phone call. This is what the round of calls used to collect, so
   * it is the reason a CAM opens a row.
   */
  answers?: ProviderAnswers;
  /**
   * The CAM override, and only that. Paying is what ordinarily makes a provider
   * visible to the family, so this is set on the one who has not paid and is being
   * let through anyway. Undefined on every row nobody has overridden, which is
   * most of them.
   */
  published?: boolean;
};

// When this row last moved, whichever step moved it. The table shows one time
// per row and this is the one worth showing: a CAM scanning the list is asking
// how long each row has been sitting where it is, not when the send went out to
// somebody who has since paid.
export const lastMovedAt = (p: PaywallProvider) =>
  p.paidAt ?? p.declinedAt ?? p.sentAt ?? p.contactedAt;

// The same answer, date only, for the table's column. The clock time belongs to
// a step rather than to the row: the drawer lists every step with its minute,
// which is where an exact time is worth reading. A column being scanned is
// answering how many days a row has been sitting where it is, and 09:12 against
// every messaged row is noise that stops the dates lining up.
export const lastMovedOn = (p: PaywallProvider) =>
  lastMovedAt(p)?.split(' · ')[0];

// What goes out to providers. Pulled from the care assessment rather than typed
// by a CAM, and editable in the publish-and-send modal before it goes: the
// assessment is the source, but a CAM reading it back can see when it is thin or
// wrong, and that is the last moment anybody looks.
export type CareRequest = {
  needs: string[];
  funding: string;
  startTiming: string;
};

// The needs a CAM can put on a request. The care circle's own conditions plus the
// ones CAM asks providers about on the phone, so the picker covers a real
// conversation rather than only what this fixture happens to hold.
export const NEEDS_OPTIONS = [
  ...CARE_CIRCLE.conditions,
  'Hoisting',
  'PEG feeding',
  'Medication support',
  'Palliative',
];

// Mirrors CareSearch's Shortlist — same id/name/status shape, so the two describe
// one thing — plus the kind of care it is for, which is what tells two of a
// circle's shortlists apart once the name has been read.
export type PaywallShortlist = {
  id: string;
  name: string;
  careType: CareType;
  location: string;
  /** What providers get sent. Edited in the modal, not on this record. */
  careRequest: CareRequest;
  status: 'draft' | 'published';
  publishedAt?: string;
  /**
   * When a CAM marked the care secured. Not a third value on `status`: the list
   * stays published with the family still looking at it, and this says the search
   * behind it is over. Undefined on every list still running, which is most of
   * them.
   */
  securedAt?: string;
  providers: PaywallProvider[];
};

export type Scenario =
  'fresh' | 'someSent' | 'firstPaid' | 'allQuiet' | 'oneDeclined';

const slug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

// Tier and capacity per provider, keyed by the name the care search pane uses.
// Hillcrest manor has no space here because it is already 'Unavailable' over
// there — the two views of the same provider have to agree.
const TIERING: Record<string, { tier: Tier; hasSpace: boolean }> = {
  'Oakwood care home': { tier: 'Premium', hasSpace: true },
  'Riverside lodge': { tier: 'Premium', hasSpace: true },
  'Cherry Tree house': { tier: 'Premium', hasSpace: true },
  'Bramble court': { tier: 'Standard', hasSpace: true },
  'Hillcrest manor': { tier: 'Standard', hasSpace: false },
};

// The inbox we hold for each provider, keyed by name like the tiering above. Not
// on the care search seed items: over there a provider is somewhere a CAM rings,
// and an address we email is only interesting once there is a link to send.
const EMAILS: Record<string, string> = {
  'Oakwood care home': 'referrals@oakwoodcare.co.uk',
  'Riverside lodge': 'enquiries@riversidelodge.co.uk',
  'Cherry Tree house': 'info@cherrytreehouse.co.uk',
  'Bramble court': 'hello@bramblecourt.co.uk',
  'Hillcrest manor': 'admin@hillcrestmanor.co.uk',
};

type Base = Pick<
  PaywallProvider,
  'id' | 'name' | 'tier' | 'hasSpace' | 'address' | 'phone' | 'email' | 'note'
>;

// Built from the care search seed items rather than a list of our own, so adding
// a provider there shows up here instead of silently diverging. Anything without
// a tier defaults to standard with space — a new provider is not premium until
// someone says so.
const BASE: Base[] = SEED_ITEMS.map((item) => ({
  id: slug(item.name),
  name: item.name,
  address: item.address,
  phone: item.phone,
  email: EMAILS[item.name] ?? 'referrals@example.co.uk',
  // The seeded notes come across too — they are the CAM's own words about this
  // provider, and they belong to the provider rather than to a surface.
  note: item.note,
  ...(TIERING[item.name] ?? { tier: 'Standard' as Tier, hasSpace: true }),
}));

const byId = (id: string) => BASE.find((p) => p.id === id)!;

// Where every provider sits before anyone has done anything. Neither tier nor
// capacity comes into it — the list is assembled, nothing has gone out.
const restingState = (_p: Base): PaywallState => 'draft';

// Per-scenario patches, keyed by provider id and merged over the resting state.
// Patches rather than whole providers so a scenario only states what it changes
// and the names and tiers stay defined once.
// One send goes to everybody, so a half-messaged list is the exception rather
// than a stage: it happens when a provider is added after the request went out.
// `someSent` is that case now, not a CAM part-way through a round of calls.
const MESSAGED_AT = '4 Aug · 09:12';

// When the seeded phone call was typed up, and the stamp every entry made in this
// session carries. Declared up here because OVERRIDES below reads it. Fixed for
// the same reason every other timestamp in this file is: a real clock would make
// every screenshot and story differ.
export const CAM_ENTRY_AT = '4 Aug · 14:20';
const OVERRIDES: Record<Scenario, Record<string, Partial<PaywallProvider>>> = {
  fresh: {},
  someSent: {
    'oakwood-care-home': { state: 'messaged', sentAt: MESSAGED_AT },
    'riverside-lodge': { state: 'messaged', sentAt: MESSAGED_AT },
    'bramble-court': { state: 'messaged', sentAt: MESSAGED_AT },
    'hillcrest-manor': { state: 'messaged', sentAt: MESSAGED_AT },
    // Added to the list after the request went out, so it is the one row with
    // a send still to do.
    'cherry-tree-house': { state: 'draft' },
  },
  firstPaid: {
    'oakwood-care-home': {
      state: 'paid',
      sentAt: MESSAGED_AT,
      paidAt: '4 Aug · 11:40',
      answers: {
        contact: 'Dawn Whitaker',
        directNumber: '01245 447 219',
        source: 'provider',
      },
    },
    'riverside-lodge': { state: 'messaged', sentAt: MESSAGED_AT },
    'cherry-tree-house': { state: 'messaged', sentAt: MESSAGED_AT },
    'bramble-court': { state: 'messaged', sentAt: MESSAGED_AT },
    'hillcrest-manor': { state: 'messaged', sentAt: MESSAGED_AT },
  },
  // Everyone messaged, nobody paid, and the honest end of the road.
  //
  // This is where a shortlist fails, and it replaced an everyone-declined
  // scenario that this product cannot reach: nothing asks a provider to say no,
  // so a provider who cannot help just does not answer. Five rows of silence is
  // what that looks like, and it is indistinguishable from five rows of no room.
  //
  // It is also the state the override exists for: the family is looking at an
  // empty shortlist, and Make visible on a row is the only thing that fills it.
  allQuiet: {
    'oakwood-care-home': { state: 'messaged', sentAt: MESSAGED_AT },
    // Rang instead of using the page, and a colleague typed it up. Still
    // `messaged`: answering a CAM on the phone is not paying, so the state does
    // not move and the lead is not released. What changed is that CAM now knows
    // something about this provider that the table cannot show.
    'riverside-lodge': {
      state: 'messaged',
      sentAt: MESSAGED_AT,
      answers: {
        contact: 'Marie Kowalski',
        directNumber: '01245 118 402',
        source: 'cam',
        enteredBy: 'Daniel Okafor',
        enteredAt: CAM_ENTRY_AT,
      },
    },
    'cherry-tree-house': { state: 'messaged', sentAt: MESSAGED_AT },
    'bramble-court': { state: 'messaged', sentAt: MESSAGED_AT },
    'hillcrest-manor': { state: 'messaged', sentAt: MESSAGED_AT },
  },
  // One no, and four nothings. What a shortlist realistically looks like once the
  // no is free: the provider who cannot help says so and stops being chased, and
  // the rest are silent. Not five nos — a provider with nothing to say still says
  // nothing, so this is the shape to design the table around.
  oneDeclined: {
    'oakwood-care-home': {
      state: 'declined',
      sentAt: MESSAGED_AT,
      declinedAt: '4 Aug · 09:31',
    },
    'riverside-lodge': { state: 'messaged', sentAt: MESSAGED_AT },
    'cherry-tree-house': { state: 'messaged', sentAt: MESSAGED_AT },
    'bramble-court': { state: 'messaged', sentAt: MESSAGED_AT },
    'hillcrest-manor': { state: 'messaged', sentAt: MESSAGED_AT },
  },
};

export const providersFor = (scenario: Scenario): PaywallProvider[] =>
  BASE.map((p) => ({
    ...p,
    state: restingState(p),
    ...OVERRIDES[scenario][p.id],
  }));

// A settled provider on an older list: messaged, paid, and so visible to the
// family. Those lists are history, so their rows don't need a lifecycle to move
// through, and they need no override either — paying is what put them in front of
// the family.
const settled = (id: string, at: string): PaywallProvider => {
  const p = byId(id);
  return {
    ...p,
    state: 'paid',
    sentAt: at,
    paidAt: at,
    answers: {
      contact: 'Dawn Whitaker',
      directNumber: '01245 447 219',
      source: 'provider',
    },
  };
};

// The request as it leaves us, per kind of care. Needs and funding come off the
// care circle's own assessment rather than being written here, so the modal shows
// a CAM what we actually hold about this person.
const DOM_REQUEST: CareRequest = {
  needs: CARE_CIRCLE.conditions,
  funding: CARE_CIRCLE.funding,
  startTiming: 'Within 2 weeks',
};

const CARE_HOME_REQUEST: CareRequest = {
  needs: CARE_CIRCLE.conditions,
  funding: CARE_CIRCLE.funding,
  startTiming: 'Within a month',
};

// A row on a care home list: rung by a CAM, with where the call got to and when.
// No paywall state at all — `state` has to be set because every provider carries
// one, and 'draft' is the honest value on a list that never sends.
const rung = (
  id: string,
  contactState: ContactStatus,
  at?: string,
): PaywallProvider => ({
  ...byId(id),
  state: 'draft',
  contactState,
  contactedAt: at,
});

export const DRAFT_LIST_ID = 'sl-jun-01';

// Newest first, the order the selector shows them in.
//
// Three lists, and they are the three things a CAM sees on this tab: a dom list
// being worked on, a care home list that this project does not touch, and an
// earlier dom list that is over. The names say the care type rather than
// numbering them, because on this tab the care type is what decides whether
// there is a send at all — "Shortlist 2" told a CAM nothing about which of the
// two tabs they were about to be looking at.
export const shortlistsFor = (
  scenario: Scenario,
  publishDraft = false,
): PaywallShortlist[] => [
  // The other half of a care circle's search, and the reason it is in here: a CAM
  // works both from the same tab, so the tab has to be honest that only one of
  // them sends. Left as a draft on purpose — side by side with the dom list, the
  // sharpest single difference is Publish against Publish and send.
  {
    id: 'sl-jun-18',
    name: 'Care homes in Edinburgh',
    careType: 'Care home',
    location: 'Edinburgh',
    careRequest: CARE_HOME_REQUEST,
    status: 'draft',
    providers: [
      rung('oakwood-care-home', 'Contacted', '18 Jun · 15:31'),
      rung('cherry-tree-house', 'Secured', '18 Jun · 15:48'),
      rung('bramble-court', 'Unavailable', '18 Jun · 15:52'),
      // Nobody has rung this one yet, so it carries no date. Every care home list
      // has rows in this state, which is what makes the column worth having.
      rung('riverside-lodge', 'Not contacted'),
    ],
  },
  {
    id: DRAFT_LIST_ID,
    name: 'Home care in Edinburgh',
    careType: 'DOM care',
    location: 'Edinburgh',
    careRequest: DOM_REQUEST,
    status: publishDraft ? 'published' : 'draft',
    publishedAt: publishDraft ? PUBLISHED_AT : undefined,
    // Nothing to set per provider. Publishing sends the links; who the family
    // can see follows from who has paid, so a pre-published fixture is the same
    // rows with the list's own status moved.
    providers: providersFor(scenario),
  },
  {
    id: 'sl-may-12',
    name: 'Home care in Leith',
    careType: 'DOM care',
    location: 'Leith',
    careRequest: DOM_REQUEST,
    status: 'published',
    publishedAt: '12 May · 11:20',
    providers: [
      settled('riverside-lodge', '12 May · 10:02'),
      settled('bramble-court', '12 May · 10:09'),
      settled('hillcrest-manor', '12 May · 10:14'),
    ],
  },
];

// Timestamps handed out in order as the CAM acts, so a click always produces a
// plausible time and the prototype stays identical between runs — a real clock
// would make every screenshot and story differ.
export const NEXT_TIMES = [
  '4 Aug · 09:34',
  '4 Aug · 09:41',
  '4 Aug · 09:52',
  '4 Aug · 10:06',
  '4 Aug · 10:18',
];

// One timestamp for the send, because it is one action: every row messaged by
// Publish and send carries the same time, which is how the table shows that they
// went out together rather than one at a time.
export const MESSAGE_ALL_AT = '4 Aug · 09:12';

export const PAID_TIMES = [
  '4 Aug · 12:02',
  '4 Aug · 13:19',
  '4 Aug · 14:47',
  '4 Aug · 16:05',
  '4 Aug · 16:52',
];

export const PUBLISHED_AT = '4 Aug · 17:10';

// When the search was closed off. Later than every other stamp in the file
// because it is the last thing that happens to a list: the care is arranged, so
// there is nothing after it.
export const SECURED_AT = '5 Aug · 11:26';
